export type PortalInboundStatus = 'render' | 'injected' | 'externalLoginError'

export type PortalInboundMessage =
  | { status: 'render' }
  | { status: 'injected'; initialRoute?: unknown }
  | { status: 'externalLoginError'; error?: unknown }

export type AuthTokensMessage = {
  status: 'authTokens'
  token: string
  refreshToken: string
  firstRender: true
}

export type PortalMessageHandlerOptions = {
  portalWindow: Window | null
  targetOrigin: string
  token: string
  refreshToken: string
  onInjected: (initialRoute: string) => void
  onError: () => void
}

const LOCAL_HOSTNAMES = ['localhost', '127.0.0.1', '::1', '[::1]']

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0

/**
 * Parses a portal message into one of the three statuses this app can
 * receive. Returns null for anything else, including authTokens, which
 * only ever travels in the opposite direction.
 */
export const parsePortalMessageData = (
  data: unknown
): PortalInboundMessage | null => {
  let parsed: unknown = data

  if (typeof data === 'string') {
    try {
      parsed = JSON.parse(data) as unknown
    } catch {
      return null
    }
  }

  if (!isRecord(parsed)) {
    return null
  }

  const status = parsed.status

  if (typeof status !== 'string') {
    return null
  }

  switch (status) {
    case 'render':
      return { status }
    case 'injected':
      return { status, initialRoute: parsed.initialRoute }
    case 'externalLoginError':
      return { status, error: parsed.error }
    default:
      return null
  }
}

/**
 * Derives the trusted child origin from the external URL supplied at
 * runtime by the auth API. Paths and trailing slashes are discarded.
 */
export const resolvePortalOrigin = (externalUrl: string): string | null => {
  if (!externalUrl) {
    return null
  }

  try {
    const url = new URL(externalUrl)

    const isLocalHttp =
      process.env.NODE_ENV === 'development' &&
      url.protocol === 'http:' &&
      LOCAL_HOSTNAMES.includes(url.hostname)

    if (url.protocol !== 'https:' && !isLocalHttp) {
      return null
    }

    return url.origin
  } catch {
    return null
  }
}

export const createPortalMessageHandler = ({
  portalWindow,
  targetOrigin,
  token,
  refreshToken,
  onInjected,
  onError,
}: PortalMessageHandlerOptions): ((event: MessageEvent) => void) => {
  return function handlePortalMessage(event: MessageEvent): void {
    if (!portalWindow) {
      return
    }

    if (event.source !== portalWindow) {
      return
    }

    if (event.origin !== targetOrigin) {
      return
    }

    const message = parsePortalMessageData(event.data)

    if (!message) {
      return
    }

    switch (message.status) {
      case 'render': {
        if (!isNonEmptyString(token) || !isNonEmptyString(refreshToken)) {
          onError()
          return
        }

        const payload: AuthTokensMessage = {
          status: 'authTokens',
          token,
          refreshToken,
          firstRender: true,
        }

        portalWindow.postMessage(JSON.stringify(payload), targetOrigin)
        return
      }

      case 'injected': {
        if (!isNonEmptyString(message.initialRoute)) {
          onError()
          return
        }

        onInjected(message.initialRoute)
        return
      }

      case 'externalLoginError': {
        onError()
        return
      }

      default:
        return
    }
  }
}
